import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription, interval } from 'rxjs';
import { CoreApiService } from '../../../core/service/core-api-service';
import { AppService } from '../../../core/service/app.service';
import { TrackingSignalrService } from '../../../core/service/tracking-signalr.service';
import { OrderAlertService } from '../../../core/service/order-alert.service';
import { properties } from '../../../../environments/properties';
import {
  ACTIVE_FOOD_ORDER_STATUS_IDS,
  FOOD_ORDER_STATUS_CONFIG,
  FoodOrderStatusConfig,
  FoodOrderStatusEnum,
  TERMINAL_FOOD_ORDER_STATUS_IDS,
} from '../../../model/food-order-status.model';

const REFRESH_INTERVAL_MS = 30000;
const PREPARING_COLUMN_STATUS_IDS = [FoodOrderStatusEnum.CONFIRMED, FoodOrderStatusEnum.PREPARING];
const READY_COLUMN_STATUS_IDS = [
  FoodOrderStatusEnum.READY,
  FoodOrderStatusEnum.ASSIGNED,
  FoodOrderStatusEnum.PICKED_UP,
];

@Component({
  selector: 'app-food-order-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TagModule,
    DialogModule,
    InputTextareaModule,
    TooltipModule,
  ],
  templateUrl: './food-order-list.component.html',
  styleUrl: './food-order-list.component.css',
})
export class FoodOrderListComponent implements OnInit, OnDestroy {
  titulo = 'Pedidos del Negocio';
  orders: any[] = [];

  showDetailDialog = false;
  selectedSummaryOrder: any = null;

  readonly statusActions: Record<
    number,
    { label: string; next: number; confirm: boolean; icon: string }[]
  > = {
    1: [
      { label: 'Confirmar', next: 2, confirm: false, icon: 'pi pi-check' },
      { label: 'Rechazar', next: 9, confirm: true, icon: 'pi pi-times' },
    ],
    2: [
      { label: 'Empezar a preparar', next: 3, confirm: false, icon: 'pi pi-play' },
      { label: 'Cancelar', next: 8, confirm: true, icon: 'pi pi-ban' },
    ],
    3: [
      { label: 'Marcar listo', next: 4, confirm: false, icon: 'pi pi-check-circle' },
      { label: 'Cancelar', next: 8, confirm: true, icon: 'pi pi-ban' },
    ],
  };

  showRejectDialog = false;
  rejectComment = '';
  orderToReject: any = null;

  storeIsOpen = false;
  togglingStore = false;

  now: number = Date.now();
  private reminderSub?: Subscription;
  private readonly subs = new Subscription();

  constructor(
    private readonly apiService: CoreApiService,
    private readonly util: AppService,
    private readonly trackingService: TrackingSignalrService,
    private readonly ngZone: NgZone,
    private readonly orderAlert: OrderAlertService,
  ) {}

  ngOnInit(): void {
    this.refresh();
    this.loadStoreStatus();

    this.subs.add(
      interval(1000).subscribe(() => {
        this.now = Date.now();
      })
    );

    // Los cambios que hace el repartidor (asignado, recogido) no llegan por
    // SignalR, así que el tablero se refresca solo (sin spinner global).
    this.subs.add(interval(REFRESH_INTERVAL_MS).subscribe(() => this.refresh(true)));

    this.subs.add(
      this.trackingService.newFoodOrder$.subscribe((order: any) => {
        this.ngZone.run(() => {
          this.orders = [order, ...this.orders];
          this.updateReminderTimer();
          this.util.mostrarToast('Nuevo pedido', `Pedido #${order.orderId} de ${order.customerName}`, 2);
        });
      })
    );

    this.subs.add(
      this.trackingService.foodOrderCancelled$.subscribe((data: any) => {
        this.ngZone.run(() => this.removeOrder(data.orderId, 8));
      })
    );

    this.subs.add(
      this.trackingService.foodOrderExpired$.subscribe((data: any) => {
        this.ngZone.run(() => this.removeOrder(data.orderId, 10));
      })
    );
  }

  private removeOrder(orderId: number, statusId: number): void {
    const order = this.orders.find((o) => o.orderId === orderId);
    if (!order) return;

    const statusName = FOOD_ORDER_STATUS_CONFIG[statusId as FoodOrderStatusEnum]?.name ?? order.statusName;
    this.orders = this.orders.filter((o) => o.orderId !== orderId);
    this.updateReminderTimer();
    this.util.mostrarToast('Pedidos', `Pedido #${orderId} → ${statusName}`, 2);
  }

  get connectionStatus$() {
    return this.trackingService.connectionStatus$;
  }

  get pendingOrders(): any[] {
    return this.orders.filter((o) => o.statusId === FoodOrderStatusEnum.PENDING);
  }

  get preparingOrders(): any[] {
    return this.orders.filter((o) => PREPARING_COLUMN_STATUS_IDS.includes(o.statusId));
  }

  get readyOrders(): any[] {
    return this.orders.filter((o) => READY_COLUMN_STATUS_IDS.includes(o.statusId));
  }

  actionsFor(order: any) {
    return this.statusActions[order?.statusId] ?? [];
  }

  // Acción que avanza el pedido a la siguiente columna (la que no es Rechazar/Cancelar).
  primaryActionFor(order: any) {
    return this.actionsFor(order).find((a) => !a.confirm);
  }

  secondaryActionsFor(order: any) {
    return this.actionsFor(order).filter((a) => a.confirm);
  }

  statusLabel(order: any): string {
    if (order.statusId === FoodOrderStatusEnum.ASSIGNED) return 'Repartidor asignado';
    if (order.statusId === FoodOrderStatusEnum.PICKED_UP) return 'En camino';
    return this.getStatusConfig(order)?.name ?? order.statusName;
  }

  trackByOrderId(_index: number, order: any): number {
    return order.orderId;
  }
  
  getStatusConfig(order: any): FoodOrderStatusConfig | undefined {
    return FOOD_ORDER_STATUS_CONFIG[order.statusId as FoodOrderStatusEnum];
  }

  formatOrderCode(orderId: number): string {
    return `000-${orderId.toString().padStart(3, '0')}`;
  }

  async updateStatus(
    order: any,
    action: { label: string; next: number; confirm: boolean; icon: string },
  ): Promise<void> {
    if (action.next === 9) {
      this.orderToReject = order;
      this.rejectComment = '';
      this.showRejectDialog = true;
      return;
    }

    if (action.confirm) {
      await this.util.mostrarConfirmacion(`¿${action.label} el pedido #${order.orderId}?`, 2);
    }

    this.sendStatusUpdate(order, action.next, undefined, () => this.closeDetail());
  }

  confirmReject(): void {
    const comment = this.rejectComment.trim();
    if (!comment) {
      this.util.mostrarToast('Pedidos', 'El motivo del rechazo es obligatorio', 3);
      return;
    }

    this.sendStatusUpdate(this.orderToReject, 9, comment, () => {
      this.showRejectDialog = false;
      this.orderToReject = null;
      this.rejectComment = '';
      this.closeDetail();
    });
  }

  cancelReject(): void {
    this.showRejectDialog = false;
    this.orderToReject = null;
    this.rejectComment = '';
  }

  openDetail(order: any): void {
    this.selectedSummaryOrder = order;
    this.showDetailDialog = true;
  }

  closeDetail(): void {
    this.showDetailDialog = false;
    this.selectedSummaryOrder = null;
  }

  private sendStatusUpdate(order: any, statusId: number, commentary?: string, onSuccess?: () => void): void {
    const payload: any = { foodOrderId: order.orderId, status: statusId };
    if (commentary) {
      payload.commentary = commentary;
    }

    this.apiService.consumirWSPut(
      properties.serviceFoodOrderUpdateStatus,
      { data: payload, fecha: new Date(), company: this.util.returnEmpresa() },
      (data) => {
        if (data.estado === properties.codeOK) {
          order.statusId = statusId;
          order.statusName =
            FOOD_ORDER_STATUS_CONFIG[statusId as FoodOrderStatusEnum]?.name ?? order.statusName;

          // Cancelar/Rechazar llevan a un estado terminal: el pedido deja de pertenecer
          // al listado activo y se quita de "En gestión", igual que ya se hace ante una
          // cancelación/expiración recibida por SignalR (ver removeOrder()).
          if (TERMINAL_FOOD_ORDER_STATUS_IDS.includes(statusId as FoodOrderStatusEnum)) {
            this.orders = this.orders.filter((o) => o.orderId !== order.orderId);
          }
          this.updateReminderTimer();

          this.util.mostrarToast('Pedidos', `Pedido #${order.orderId} → ${order.statusName}`, 1);
          onSuccess?.();
        } else {
          this.util.mostrarToast('Pedidos', data.mensaje || 'No se pudo actualizar el estado', 4);
        }
      },
    );
  }

  refresh(silent = false): void {
    this.loadOrders(silent);
  }

  loadOrders(silent = false): void {
    this.apiService.consumirWSPost(
      properties.serviceFoodOrdersByStore,
      { data: { statusIds: ACTIVE_FOOD_ORDER_STATUS_IDS }, fecha: new Date(), company: this.util.returnEmpresa() },
      (data) => {
        if (data.estado === properties.codeOK) {
          this.orders = data.data ?? [];
          this.updateReminderTimer();
        } else if (!silent) {
          this.util.mostrarToast('Pedidos', 'Error al obtener los pedidos', 4);
        }
      },
      silent,
    );
  }

  getRemainingSeconds(order: any): number {
    if (!order?.createdAt) return 0;
    const createdTime = new Date(order.createdAt).getTime();
    const elapsedSeconds = Math.floor((this.now - createdTime) / 1000);
    return 180 - elapsedSeconds;
  }

  isCountdownExpired(order: any): boolean {
    return this.getRemainingSeconds(order) <= 0;
  }

  getCountdownText(order: any): string {
    const remaining = this.getRemainingSeconds(order);
    if (remaining <= 0) {
      return '00:00';
    }
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  updateReminderTimer(): void {
    const hasPending = this.pendingOrders.length > 0;
    if (hasPending && !this.reminderSub) {
      this.reminderSub = interval(60000).subscribe(() => {
        if (this.pendingOrders.length > 0) {
          this.orderAlert.playReminderSound();
        } else {
          this.stopReminder();
        }
      });
    } else if (!hasPending && this.reminderSub) {
      this.stopReminder();
    }
  }

  stopReminder(): void {
    this.reminderSub?.unsubscribe();
    this.reminderSub = undefined;
  }

  private loadStoreStatus(): void {
    this.apiService.consumirWSGet(
      properties.serviceGetMyStoreStatus,
      {},
      (data) => {
        if (data.estado === properties.codeOK && data.data?.sto_is_open) {
          this.storeIsOpen = true;
          this.trackingService.ensureConnected(this.util.returnToken());
          this.trackingService.openStore();
        } else {
          this.storeIsOpen = false;
        }
      }
    );
  }

  async abrirTienda(): Promise<void> {
    this.togglingStore = true;
    this.trackingService.ensureConnected(this.util.returnToken());

    try {
      await this.trackingService.openStore();
      this.storeIsOpen = true;
    } catch {
      this.util.mostrarToast('Pedidos', 'No se pudo abrir el negocio, intentá de nuevo', 4);
    } finally {
      this.togglingStore = false;
    }
  }

  async cerrarTienda(): Promise<void> {
    this.togglingStore = true;

    try {
      await this.trackingService.closeStore();
      this.storeIsOpen = false;
      this.trackingService.stopConnection();
    } catch {
      this.util.mostrarToast('Pedidos', 'No se pudo cerrar el negocio, intentá de nuevo', 4);
    } finally {
      this.togglingStore = false;
    }
  }

  ngOnDestroy(): void {
    this.stopReminder();
    this.subs.unsubscribe();
  }
}
